import {
  createContext,
  ReactNode,
  useContext,
  useEffect,
  useState,
} from 'react';
import { useSearchParams } from 'react-router-dom';

import { TENANT_CODE_PARAM } from '../components/lib';
import { Feature } from '../utils/features-constants';
import { useAuth } from './AuthContext';
import {
  useGetTenantFeaturesQuery,
  useGetTenantQuery,
  useGetUserFeaturesQuery,
} from '../generated-mesh/graphql';

/**
 * All feature flags are tracked here: https://www.notion.so/futurefitai/Feature-Flag-Inventory-20a533696203499eb3df27f7a92ef9b7?pvs=4.
 * If any changes are made please ensure to update the inventory so we have an up-to-date list of
 * feature flags.
 */
export type FeatureContextType = {
  features: string[];
  isAdditionalLangsSpanishPortugueseEnabled?: boolean;
  isEmployerPortalEnabled?: boolean;
  isFormsImprovementsEnabled?: boolean;
  isFormsV3DefaultQuestionsEnabled?: boolean;
  isHideIntercomEnabled?: boolean;
  isLoading?: boolean;
  isRecruiterOutreachEnabled?: boolean;
  isLiveTranslatorEnable?: boolean;
  isLLMResumeServiceEnabled?: boolean;
  isLanguageFilterEnabled?: boolean;
  isLookerInsightsEnabled?: boolean;
  isLearningSidePanelSkillsAndCareersEnabled?: boolean;
  isInviteLinksValidateEmailDomainsEnabled?: boolean;
  isHideCareerDemandEnabled?: boolean;
  isATSEasyApplyEnabled?: boolean;
  isPolicyDisplaySignUpEnabled?: boolean;
  isLearningResourceHideCostFilterEnabled?: boolean;
  isUseLLMParsedSkillsEnabled?: boolean;
  isRoleBasedPermissionsEnabled?: boolean;
  isSatisfactionRatingsEnabled?: boolean;
  isOnboardingSimplificationEnabled?: boolean;
  isCredentialsUpdateEnabled?: boolean;
  isLiveLmiDataEnabled?: boolean;
  isEducationTalentFiltersEnabled?: boolean;
  isManageContentEnabled?: boolean;
  isAllowRestrictNextStepByTagsEnabled?: boolean;
  isBusinessResourcesEnabled?: boolean;
  isCourseLLMGuardrailsEnabled?: boolean;
  isParentCredentialsFilterEnabled?: boolean;
  isCandidateRecommenderGuardrailEnabled?: boolean;
  isFeatureIndividualJobEnabled?: boolean;
  isHomePageV2Enabled?: boolean;
  isDownvoteRecEnabled?: boolean;
  isHomePageV2ImagesEnabled?: boolean;
  isSearchJobsV3Enabled?: boolean;
  isSearchJobsV3FindJobsEnabled?: boolean;
  isFeaturedEmployerEnabled?: boolean;
  isJobToolsEnabled?: boolean;
  isCandidateRecommenderRedesignEnabled?: boolean;
  isSkillsAtlasSearchEnabled?: boolean;
  isHomepagePersonaOrderingEnabled?: boolean;
  isTalentRadiusFilterEnabled?: boolean;
  isInterviewPrepEnabled?: boolean;
  isEmployerSelfOnboardingEnabled?: boolean;
  isWorkBasedLearningEnabled?: boolean;
  isStaffCoachEnabled?: boolean;
};

export const FeatureContext = createContext<FeatureContextType>({
  features: [],
});

export const FeatureProvider = ({ children }: { children: ReactNode }) => {
  const { user } = useAuth();
  const [features, setFeatures] = useState<string[]>();
  const [{ data: userFeaturesData, fetching: userFeaturesFetching }] =
    useGetUserFeaturesQuery({
      pause: !user.userId,
    });

  const [searchParams] = useSearchParams();
  const tenantCodeInParams = searchParams.get(TENANT_CODE_PARAM);

  const [{ data: tenantData, fetching: tenantFetching }] = useGetTenantQuery({
    variables: { tenantCode: tenantCodeInParams! },
    pause: !!user.userId || !tenantCodeInParams,
  });

  const [{ data: tenantFeaturesData, fetching: tenantFeaturesFetching }] =
    useGetTenantFeaturesQuery({
      variables: {
        tenant_uuid: tenantData?.getTenant?.id!,
      },
      pause:
        !!user.userId ||
        !tenantCodeInParams ||
        tenantFetching ||
        !tenantData?.getTenant,
    });

  const featuresData = !user.userId
    ? tenantFeaturesData?.getTenantFeatures
    : userFeaturesData?.getUserFeatures;

  /**
   * Grab all feature flags for the user
   */
  useEffect(() => {
    const featureSlugs = featuresData?.map((f) => f.slug);
    setFeatures(featureSlugs);
  }, [featuresData]);

  const value: FeatureContextType = {
    features: features || [],
    isAdditionalLangsSpanishPortugueseEnabled: features?.includes(
      Feature.ADDITIONAL_LANGS_ES_PT
    ),
    isEmployerPortalEnabled:
      features?.includes(Feature.EMPLOYER_PORTAL) || false,
    isFormsImprovementsEnabled:
      features?.includes(Feature.FORMS_IMPROVEMENTS) || false,
    isFormsV3DefaultQuestionsEnabled:
      features?.includes(Feature.FORMS_V3_DEFAULT_QUESTIONS) || false,
    isHideIntercomEnabled: features?.includes(Feature.HIDE_INTERCOM) || false,
    isLoading: userFeaturesFetching || tenantFeaturesFetching || tenantFetching,
    isRecruiterOutreachEnabled:
      features?.includes(Feature.RECRUITER_OUTREACH) || false,
    isLiveTranslatorEnable:
      features?.includes(Feature.LIVE_TRANSLATION) || false,
    isLLMResumeServiceEnabled:
      features?.includes(Feature.LLM_RESUME_SERVICE) || false,
    isLanguageFilterEnabled:
      features?.includes(Feature.LANGUAGE_FILTER) || false,
    isLookerInsightsEnabled:
      features?.includes(Feature.LOOKER_INSIGHTS) || false,
    isLearningSidePanelSkillsAndCareersEnabled:
      features?.includes(Feature.LEARNING_SIDE_PANEL_SKILLS_AND_CAREERS) ||
      false,
    isInviteLinksValidateEmailDomainsEnabled:
      features?.includes(Feature.INVITE_LINKS_VALIDATE_EMAIL_DOMAINS) || false,
    isHideCareerDemandEnabled:
      features?.includes(Feature.HIDE_CAREER_DEMAND) || false,
    isATSEasyApplyEnabled: features?.includes(Feature.ATS_EASY_APPLY) || false,
    isPolicyDisplaySignUpEnabled:
      features?.includes(Feature.POLICY_DISPLAY_SIGN_UP) || false,
    isLearningResourceHideCostFilterEnabled:
      features?.includes(Feature.LEARNING_RESOURCE_HIDE_COST_FILTER) || false,
    isUseLLMParsedSkillsEnabled:
      features?.includes(Feature.USE_LLM_PARSED_SKILLS) || false,
    isRoleBasedPermissionsEnabled:
      features?.includes(Feature.ROLE_BASED_PERMISSIONS) || false,
    isSatisfactionRatingsEnabled:
      features?.includes(Feature.SATISFACTION_RATINGS) || false,
    isOnboardingSimplificationEnabled:
      features?.includes(Feature.ONBOARDING_SIMPLIFICATION) || false,
    isCredentialsUpdateEnabled:
      features?.includes(Feature.CREDENTIALS_UPDATE) || false,
    isLiveLmiDataEnabled: features?.includes(Feature.LIVE_LMI_DATA) || false,
    isEducationTalentFiltersEnabled:
      features?.includes(Feature.EDUCATION_TALENT_FILTERS) || false,
    isManageContentEnabled: features?.includes(Feature.MANAGE_CONTENT) || false,
    isAllowRestrictNextStepByTagsEnabled:
      features?.includes(Feature.ALLOW_RESTRICT_NEXT_STEP_BY_TAGS) || false,
    isBusinessResourcesEnabled:
      features?.includes(Feature.BUSINESS_RESOURCES) || false,
    isCourseLLMGuardrailsEnabled:
      features?.includes(Feature.COURSE_LLM_GUARDRAILS) || false,
    isParentCredentialsFilterEnabled:
      features?.includes(Feature.PARENT_CREDENTIALS_FILTER) || false,
    isCandidateRecommenderGuardrailEnabled:
      features?.includes(Feature.CANDIDATE_RECOMMENDER_GUARDRAIL) || false,
    isFeatureIndividualJobEnabled:
      features?.includes(Feature.FEATURE_INDIVIDUAL_JOB) || false,
    isHomePageV2Enabled: features?.includes(Feature.HOMEPAGE_V2) || false,
    isDownvoteRecEnabled:
      features?.includes(Feature.DOWNVOTE_RECOMMENDATIONS) || false,
    isHomePageV2ImagesEnabled:
      features?.includes(Feature.HOMEPAGE_V2_IMAGES) || false,
    isSearchJobsV3Enabled: features?.includes(Feature.SEARCH_JOBS_V3) || false,
    isSearchJobsV3FindJobsEnabled:
      features?.includes(Feature.SEARCH_JOBS_V3_FIND_JOBS) || false,
    isFeaturedEmployerEnabled:
      features?.includes(Feature.FEATURED_EMPLOYER) || false,
    isJobToolsEnabled: features?.includes(Feature.JOB_TOOLS) || false,
    isInterviewPrepEnabled: features?.includes(Feature.INTERVIEW_PREP) || false,
    isCandidateRecommenderRedesignEnabled:
      features?.includes(Feature.CANDIDATE_RECOMMENDER_REDESIGN) || false,
    isSkillsAtlasSearchEnabled:
      features?.includes(Feature.SKILLS_ATLAS_SEARCH) || false,
    isHomepagePersonaOrderingEnabled:
      features?.includes(Feature.HOMEPAGE_PERSONA_ORDERING) || false,
    isTalentRadiusFilterEnabled:
      features?.includes(Feature.TALENT_RADIUS_FILTER) || false,
    isEmployerSelfOnboardingEnabled:
      features?.includes(Feature.EMPLOYER_SELF_ONBOARDING) || false,
    isWorkBasedLearningEnabled:
      features?.includes(Feature.WORK_BASED_LEARNING) || false,
    isStaffCoachEnabled: features?.includes(Feature.STAFF_COACH) || false,
  };

  return (
    <FeatureContext.Provider value={value}>{children}</FeatureContext.Provider>
  );
};

/**
 * Get a list of toggleable features available for the user's tenant,
 * @example const { isWorkRecommenderEnabled } = useFeatures(); // true
 *          const { features } = useFeatures(); // ['work-recommender', 'employer-portal']
 */
export const useFeatures = () => {
  const context = useContext(FeatureContext);
  if (context === undefined) {
    throw new Error('useFeatures must be used within a FeatureProvider');
  }
  return context;
};
